/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useHotelHub } from "../providers/signalr/HotelHubProvider";
import { useHotel } from "./HotelContext";
import { logger } from "../utils/logger";
import { translationService } from "../services/core/translationService";
import { useUnifiedNotificationStore } from "../stores/unifiedNotificationStore";
import { useAudioNotifications } from "../hooks/useAudioNotifications";

interface NotificationContextType {
  connectionStatus: string; // Simplified from HubConnectionState
  newOrderCount: number;
  newLateCheckoutCount: number;
  newServiceRequestCount: number;
  clearNewOrderCount: () => void;
  clearNewLateCheckoutCount: () => void;
  clearNewServiceRequestCount: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const { on, off, isConnected } = useHotelHub();
  const { myHotelInfo } = useHotel();
  const { t, i18n } = useTranslation();

  // Get counts from unified store instead of maintaining local state
  const {
    pendingOrdersCount,
    pendingLateCheckoutsCount,
    pendingServiceRequestsCount,
  } = useUnifiedNotificationStore();

  const [translationsLoaded, setTranslationsLoaded] = useState<boolean>(false);
  const { play: playAudioNotification } = useAudioNotifications();

  // Load Admin.general and Service namespaces
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        await translationService.loadNamespaces(i18n.language, [
          "Admin.general",
          "Service",
        ]);
        logger.log(
          "NotificationContext: Loaded Admin.general and Service namespaces",
        );
        setTranslationsLoaded(true);
      } catch (error) {
        logger.error("NotificationContext: Failed to load namespaces:", error);
        // Set loaded anyway to prevent blocking
        setTranslationsLoaded(true);
      }
    };

    if (i18n.language) {
      loadTranslations();
    }
  }, [i18n.language]);

  // Function to fetch pending counts - now a no-op since UnifiedNotificationProvider handles this
  const fetchPendingCounts = useCallback(async () => {
    // This is now handled by UnifiedNotificationProvider
    // Keeping the function for backward compatibility
    logger.log(
      "NotificationContext: fetchPendingCounts called - handled by UnifiedNotificationProvider",
    );
  }, []);

  // Set up SignalR event handlers
  useEffect(() => {
    logger.log("NotificationContext: SignalR setup effect", {
      isConnected,
      hotelId: myHotelInfo?.hotelId,
      translationsLoaded,
    });
    if (!isConnected || !myHotelInfo?.hotelId || !translationsLoaded) return;

    const handleNewOrderNotification = (
      orderId: string,
      roomNumber: string,
    ) => {
      logger.log(
        `SignalR: New Order Received - ID: ${orderId}, Room: ${roomNumber}`,
      );
      if (myHotelInfo?.hotelId) fetchPendingCounts();
      playAudioNotification("newOrder");
      toast.info(
        t("notification.newOrder", {
          roomNumber,
          defaultValue: `New order received for Room ${roomNumber}. Refresh to see details.`,
        }),
      );
    };

    const handleLateCheckoutNotification = (
      requestId: string,
      roomNumber: string,
    ) => {
      // console.log(`SignalR: New Late Checkout Request Received - ID: ${requestId}, Room: ${roomNumber}`);
      if (myHotelInfo?.hotelId) fetchPendingCounts();
      playAudioNotification("newRequest");
      toast.info(
        t("notification.newLateCheckout", {
          roomNumber,
          defaultValue: `New late checkout request for Room ${roomNumber}. Refresh to see details.`,
        }),
      );
    };

    const handleServiceRequestNotification = (
      serviceRequestId: string,
      roomNumber: string,
      serviceType: string,
    ) => {
      // console.log(`SignalR: New Service Request Received - ID: ${serviceRequestId}, Room: ${roomNumber}, Type: ${serviceType}`);
      if (myHotelInfo?.hotelId) fetchPendingCounts();
      playAudioNotification("newRequest");
      // Translate the service type
      const translatedServiceType = t(
        `service_type_${serviceType.toLowerCase()}`,
        serviceType,
      );
      toast.info(
        t("notification.newServiceRequest", {
          roomNumber,
          serviceType: translatedServiceType,
          defaultValue: `New service request (${translatedServiceType}) for Room ${roomNumber}. Refresh to see details.`,
        }),
      );
    };

    // Count updates are now handled by UnifiedNotificationProvider

    const handleNewCheckinNotification = (
      stayId: string,
      roomNumber: string,
      guestName?: string,
    ) => {
      if (myHotelInfo?.hotelId) fetchPendingCounts();
      const name =
        guestName && guestName.trim().length > 0
          ? guestName
          : t("admin_checkins_guest_anonymous", "Guest");
      toast.info(
        t("admin_checkins_new_toast", {
          roomNumber,
          guestName: name,
          defaultValue: `New check-in — Room ${roomNumber}${name ? ` — ${name}` : ""}.`,
        }),
      );
    };

    const handleNewTransferRequestNotification = (
      requestId: string,
      roomNumber: string,
      destinationName: string,
    ) => {
      logger.log(
        `SignalR: New Transfer Request Received - ID: ${requestId}, Room: ${roomNumber}, Destination: ${destinationName}`,
      );
      playAudioNotification("newRequest");
      toast.info(
        t("notification.newTransferRequest", {
          roomNumber,
          destinationName,
          defaultValue: `New transfer request from Room ${roomNumber} to ${destinationName}. Refresh to see details.`,
        }),
      );
    };

    // Register event handlers for toast notifications only
    // Count updates are handled by UnifiedNotificationProvider
    logger.log(
      "NotificationContext: Registering SignalR event handlers for toast notifications",
    );
    on("ReceiveNewOrderNotification", handleNewOrderNotification);
    on("ReceiveLateCheckoutNotification", handleLateCheckoutNotification);
    on(
      "ReceiveNewServiceRequestNotification",
      handleServiceRequestNotification,
    );
    on(
      "ReceiveNewTransferRequestNotification",
      handleNewTransferRequestNotification,
    );
    on("ReceiveNewCheckinNotification", handleNewCheckinNotification);

    // Cleanup
    return () => {
      off("ReceiveNewOrderNotification", handleNewOrderNotification);
      off("ReceiveLateCheckoutNotification", handleLateCheckoutNotification);
      off(
        "ReceiveNewServiceRequestNotification",
        handleServiceRequestNotification,
      );
      off(
        "ReceiveNewTransferRequestNotification",
        handleNewTransferRequestNotification,
      );
      off("ReceiveNewCheckinNotification", handleNewCheckinNotification);
    };
  }, [
    isConnected,
    myHotelInfo,
    on,
    off,
    fetchPendingCounts,
    t,
    translationsLoaded,
    playAudioNotification,
  ]);

  // Global handler for chat message audio notifications
  // This ensures audio plays on ALL pages including the chat page
  useEffect(() => {
    if (!isConnected) return;

    // Handler for ReceiveMessage events (simple format)
    const handleChatMessage = (
      _senderName: string,
      _roomId: string,
      _message: string,
      _timestamp: string,
      isStaffMessage: boolean
    ) => {
      // Only play audio for guest messages (not staff messages)
      if (!isStaffMessage) {
        logger.log("NotificationContext: Playing chat audio for guest message");
        playAudioNotification("newChat");
      }
    };

    // Handler for ReceiveMessageWithReply events (object format)
    const handleChatMessageWithReply = (payload: {
      isStaffMessage: boolean;
      [key: string]: unknown;
    }) => {
      // Only play audio for guest messages (not staff messages)
      if (!payload.isStaffMessage) {
        logger.log("NotificationContext: Playing chat audio for guest message (with reply)");
        playAudioNotification("newChat");
      }
    };

    logger.log("NotificationContext: Registering global chat message handlers for audio");
    on("ReceiveMessage", handleChatMessage);
    on("ReceiveMessageWithReply", handleChatMessageWithReply);

    return () => {
      off("ReceiveMessage", handleChatMessage);
      off("ReceiveMessageWithReply", handleChatMessageWithReply);
    };
  }, [isConnected, on, off, playAudioNotification]);

  // Polling is now handled by UnifiedNotificationProvider using React Query

  // Clear functions are no-ops since counts are managed by unified store
  const clearNewOrderCount = () => {
    // Managed by unified store
  };
  const clearNewLateCheckoutCount = () => {
    // Managed by unified store
  };
  const clearNewServiceRequestCount = () => {
    // Managed by unified store
  };

  const contextValue: NotificationContextType = {
    connectionStatus: isConnected ? "Connected" : "Disconnected",
    newOrderCount: pendingOrdersCount,
    newLateCheckoutCount: pendingLateCheckoutsCount,
    newServiceRequestCount: pendingServiceRequestsCount,
    clearNewOrderCount,
    clearNewLateCheckoutCount,
    clearNewServiceRequestCount,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};
